import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { 
  Users, 
  MapPin, 
  ShieldCheck, 
  Sparkles,
  ChevronRight,
  UserPlus
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL;

const NewMembersSection = () => {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecentMembers();
  }, []);

  const fetchRecentMembers = async () => {
    try {
      const response = await fetch(`${API}/api/members/recent?limit=12`);
      if (response.ok) {
        const data = await response.json();
        setMembers(data.members);
      }
    } catch (error) {
      console.error('Error fetching recent members:', error);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (member) => {
    const name = member.display_name || member.username || 'U';
    return name.slice(0, 2).toUpperCase();
  };

  if (loading) {
    return (
      <div className="py-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (members.length === 0) {
    return null;
  }

  return (
    <section className="py-8" data-testid="new-members-section">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-primary" />
          <h2 className="text-xl font-bold">New Members</h2>
          <Badge variant="secondary" className="ml-2">
            {members.length} recent
          </Badge>
        </div>
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => navigate('/members')}
          className="text-primary"
        >
          View All
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {members.map((member) => (
          <Card 
            key={member.id}
            className="cursor-pointer hover:shadow-md transition-shadow overflow-hidden group"
            onClick={() => navigate(`/profile/${member.username}`)}
            data-testid={`member-card-${member.id}`}
          >
            <CardContent className="p-4 text-center">
              <div className="relative inline-block mb-3">
                <Avatar className="w-16 h-16 mx-auto border-2 border-background shadow-sm group-hover:border-primary transition-colors">
                  <AvatarImage src={member.avatar_url} alt={member.username} />
                  <AvatarFallback className="bg-primary/10 text-primary font-medium">
                    {getInitials(member)}
                  </AvatarFallback>
                </Avatar>
                {/* Badges */}
                <div className="absolute -bottom-1 -right-1 flex gap-0.5">
                  {member.identity_verified && (
                    <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center" title="Verified">
                      <ShieldCheck className="w-3 h-3 text-white" />
                    </div>
                  )}
                  {member.is_beta_user && (
                    <div className="w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center" title="Beta Tester">
                      <Sparkles className="w-3 h-3 text-white" />
                    </div>
                  )}
                </div>
              </div>
              
              <h3 className="font-medium text-sm truncate">
                {member.display_name || member.username}
              </h3>
              <p className="text-xs text-muted-foreground truncate">
                @{member.username}
              </p>
              
              {member.location && (
                <p className="text-xs text-muted-foreground mt-1 flex items-center justify-center gap-1 truncate">
                  <MapPin className="w-3 h-3" />
                  {member.location}
                </p>
              )}
              
              <p className="text-xs text-muted-foreground mt-2">
                Joined {formatDistanceToNow(new Date(member.created_at), { addSuffix: true })}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
};

export default NewMembersSection;
